/**
 * Registers the sync-message service worker and waits for it to control the
 * page. Call this once at app startup before any code that creates a
 * WorkerApi / syncMessage channel.
 *
 * Handles:
 * - Stale SW cleanup (registered but not controlling the page)
 * - updateViaCache: 'none' to bypass HTTP cache on SW fetch
 * - Logging lifecycle events (updatefound, controllerchange)
 */
export interface RegisterSyncMessageServiceWorkerOptions {
  mode?: 'startup' | 'runtime';
  /**
   * Invoked when the fire-and-forget `registration.update()` call has not
   * settled within {@link UPDATE_HANG_THRESHOLD_MS}. `update()` is not awaited
   * defensively — its promise can be left pending (a wedged service worker, a
   * registration being torn down, a stalled update fetch), which would
   * otherwise freeze the readiness gate; this was observed on Firefox (#3356).
   * This hook exists so callers can record how often that happens. Being called
   * means "did not settle in time" — usually a true hang, occasionally just a
   * slow revalidation. Omitted → no-op.
   */
  onUpdateTimeout?: (info: {waitedMs: number}) => void;
}

const SYNC_MESSAGE_SW_SCRIPT = 'sync-message-sw.js';
const CONTROLLER_WAIT_TIMEOUT_MS = 5000;
/**
 * How long to wait for a fire-and-forget `registration.update()` to settle
 * before treating it as hung and invoking `onUpdateTimeout`. Generous so a
 * merely slow (but eventually-resolving) revalidation is unlikely to trip it.
 */
export const UPDATE_HANG_THRESHOLD_MS = 15000;
let listenersAttached = false;

function isSyncMessageController(): boolean {
  const controllerScriptUrl = navigator.serviceWorker.controller?.scriptURL;
  return Boolean(
    controllerScriptUrl && controllerScriptUrl.includes(SYNC_MESSAGE_SW_SCRIPT),
  );
}

function attachLifecycleListeners() {
  if (listenersAttached) {
    return;
  }
  listenersAttached = true;

  // Bridge: surface SW-side logs in the page console so they're visible
  // without opening the SW's dedicated DevTools context.
  // @see https://developer.mozilla.org/en-US/docs/Web/API/ServiceWorkerContainer/message_event
  navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
    if (event.data?.type === 'SYNC_MESSAGE_SERVICE_WORKER_LOG') {
      // console.log('[SyncMessage SW (Internal)]', event.data.msg);
    }
  });

  // @see https://developer.mozilla.org/en-US/docs/Web/API/ServiceWorkerContainer/controllerchange_event
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    console.log('[SyncMessage SW] Controller changed — new SW active');
  });
}

async function waitForSyncMessageController(timeoutMs: number): Promise<boolean> {
  if (isSyncMessageController()) {
    return true;
  }

  return await new Promise((resolve) => {
    const timeoutId = setTimeout(() => {
      navigator.serviceWorker.removeEventListener(
        'controllerchange',
        onControllerChange,
      );
      resolve(false);
    }, timeoutMs);

    const onControllerChange = () => {
      if (!isSyncMessageController()) {
        return;
      }
      clearTimeout(timeoutId);
      navigator.serviceWorker.removeEventListener(
        'controllerchange',
        onControllerChange,
      );
      resolve(true);
    };

    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
  });
}

export async function registerSyncMessageServiceWorker(
  {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- mode is used by the commented-out stale-registration cleanup block below
    mode = 'startup',
    onUpdateTimeout,
  }: RegisterSyncMessageServiceWorkerOptions = {},
): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    console.warn('[SyncMessage SW] Service workers not supported');
    return;
  }

  // if (mode === 'startup') {
  //   // Clean up stale registrations (dev edge case: SW registered but not
  //   // controlling, e.g. after clearing site data without unregistering).
  //   // Skip if there is an active transition (installing/waiting) to avoid
  //   // tearing down a registration that is about to take control.
  //   const existingRegistrations = await navigator.serviceWorker.getRegistrations();
  //   for (const reg of existingRegistrations) {
  //     if (
  //       reg.active?.scriptURL.includes(SYNC_MESSAGE_SW_SCRIPT) &&
  //       !navigator.serviceWorker.controller &&
  //       !reg.installing &&
  //       !reg.waiting
  //     ) {
  //       console.log('[SyncMessage SW] Found stale registration, unregistering…');
  //       await reg.unregister();
  //     }
  //   }
  // }

  // Register all event listeners BEFORE calling register() so we don't miss
  // install/activate broadcasts from the SW.
  attachLifecycleListeners();

  const registration = await navigator.serviceWorker.register(
    '/sync-message-sw.js',
    { scope: '/', updateViaCache: 'none' },
  );
  console.log(`[SyncMessage SW] Registered at ${new Date().toISOString()}. Scope: ${registration.scope}`);

  // Proactively re-fetch the SW script so a newer deployed version is picked
  // up, but DO NOT block startup on it — fire-and-forget, defensively.
  // `registration.update()` resolves only when the update job completes, and
  // that promise can be left pending (a wedged service worker, a registration
  // being torn down, a stalled update fetch). If we awaited it, a pending
  // promise would freeze this readiness gate: `CodeContext.loadCode` would
  // await forever, the Pyodide worker would never be created, and the IDE Run
  // button would be stuck on a disabled "Loading". We observed exactly this on
  // Firefox (update() stayed pending for 50s+ and never returned) — see #3356.
  // Not awaiting loses nothing: update() is only an optimization, since the SW
  // self-activates via skipWaiting()/clients.claim() and browsers also re-check
  // for updates on navigation.
  // `onUpdateTimeout` records how often the promise stays pending.
  // @see https://developer.mozilla.org/en-US/docs/Web/API/ServiceWorkerRegistration/update
  const updateHangTimer = setTimeout(() => {
    onUpdateTimeout?.({waitedMs: UPDATE_HANG_THRESHOLD_MS});
  }, UPDATE_HANG_THRESHOLD_MS);
  void registration
    .update()
    .then(() => {
      clearTimeout(updateHangTimer);
    })
    .catch(() => {
      clearTimeout(updateHangTimer);
      // Registration may have been uninstalled during hot reload or by another tab.
      console.log('[SyncMessage SW] Update check failed (registration may have been uninstalled)');
    });

  // Log when the browser detects a new SW version (e.g. after a deploy while
  // the tab is still open). The SW activates itself via skipWaiting() — we
  // don't need to send a message.
  // @see https://developer.mozilla.org/en-US/docs/Web/API/ServiceWorkerRegistration/updatefound_event
  registration.addEventListener('updatefound', () => {
    // The "installing worker" is the SW that the browser is currently installing.
    // @see https://w3c.github.io/ServiceWorker/#dfn-installing-worker
    const installingWorker = registration.installing;
    if (!installingWorker) {
      return;
    }
    installingWorker.addEventListener('statechange', () => {
      if (installingWorker.state === 'installed') {
        console.log('[SyncMessage SW] New version installed');
      }
    });
  });

  await navigator.serviceWorker.ready;

  // On a hard refresh the browser clears navigator.serviceWorker.controller,
  // and the activate event (which calls clients.claim()) won't re-fire for an
  // already-active SW. Ask the SW to re-claim so it controls this page.
  if (!navigator.serviceWorker.controller && registration.active) {
    registration.active.postMessage({type: 'CLAIM_CLIENTS'});
  }

  const hasSyncController = await waitForSyncMessageController(
    CONTROLLER_WAIT_TIMEOUT_MS,
  );
  if (!hasSyncController) {
    throw new Error(
      '[SyncMessage SW] Ready resolved but sync-message-sw.js is not controlling this page.',
    );
  }

  // Fetch the SW's self-reported version+timestamp for log correlation.
  // The /version endpoint is served by the SW itself (see sync-message-sw.js).
  // Retry a few times — there's a brief window after `ready` resolves where
  // the SW may not yet be intercepting fetch events.
  const versionUrl = `${window.location.origin}/__SyncMessageServiceWorkerInput__/version`;
  const MAX_RETRIES = 3;
  const RETRY_DELAY_MS = 200;
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      const res = await fetch(versionUrl);
      const {version, createdAt} = await res.json();
      console.log(`[SyncMessage SW] Ready and controlling page (${version} / ${createdAt})`);
      return;
    } catch {
      if (attempt < MAX_RETRIES - 1) {
        await new Promise(r => setTimeout(r, RETRY_DELAY_MS));
      }
    }
  }
  console.log('[SyncMessage SW] Ready and controlling page (version probe unavailable)');
}
